/**
 * Resume PDF download helper.
 *
 * Uses an iframe + window.print() approach to generate a clean PDF
 * from the resume preview HTML without the surrounding app UI.
 */

import type { ResumeSections, ProfileInfo } from './resumeDocx';
import { emitToast } from '../lib/toastBus';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function downloadResumePdf(
  sections: ResumeSections,
  profile: ProfileInfo,
  jobTitle: string,
  company: string,
): void {
  const name = escapeHtml(profile.full_name || 'Candidate');

  const contactParts: string[] = [];
  if (profile.email) contactParts.push(escapeHtml(profile.email));
  if (profile.phone) contactParts.push(escapeHtml(profile.phone));
  if (profile.location) contactParts.push(escapeHtml(profile.location));
  if (profile.linkedin_url) contactParts.push(escapeHtml(profile.linkedin_url));

  let html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>${name} Resume</title>
<style>
  @page { margin: 0.5in 0.6in; size: letter; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: #1a1a1a; line-height: 1.4; }
  .header { text-align: center; margin-bottom: 16px; }
  .header h1 { font-size: 20pt; font-weight: 700; margin-bottom: 4px; }
  .header .contact { font-size: 9.5pt; color: #555; }
  .section-heading { font-size: 12pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #1a1a1a; border-bottom: 1px solid #333; padding-bottom: 3px; margin-top: 14px; margin-bottom: 8px; }
  .summary { font-style: italic; margin-bottom: 8px; }
  .competencies { margin-bottom: 8px; }
  .exp-header { display: flex; justify-content: space-between; align-items: baseline; margin-top: 10px; margin-bottom: 4px; }
  .exp-header .title { font-weight: 700; }
  .exp-header .dates { font-style: italic; color: #666; font-size: 10pt; }
  ul { padding-left: 18px; margin-bottom: 6px; }
  li { margin-bottom: 2px; }
  .edu-row { display: flex; justify-content: space-between; margin-bottom: 4px; }
  .skills-row { margin-bottom: 3px; }
  .skills-row strong { font-weight: 700; }
</style>
</head><body>`;

  // Header
  html += `<div class="header"><h1>${name}</h1>`;
  if (contactParts.length) {
    html += `<div class="contact">${contactParts.join('  &bull;  ')}</div>`;
  }
  html += `</div>`;

  // Professional Summary
  if (sections.professional_summary) {
    html += `<div class="section-heading">Professional Summary</div>`;
    html += `<div class="summary">${escapeHtml(sections.professional_summary)}</div>`;
  }

  // Core Competencies
  if (sections.core_competencies?.length) {
    html += `<div class="section-heading">Core Competencies</div>`;
    html += `<div class="competencies">${sections.core_competencies.map(s => escapeHtml(s)).join('  &bull;  ')}</div>`;
  }

  // Experience
  if (sections.experience?.length) {
    html += `<div class="section-heading">Professional Experience</div>`;
    for (const exp of sections.experience) {
      html += `<div class="exp-header"><div><span class="title">${escapeHtml(exp.title)}</span> &mdash; ${escapeHtml(exp.company)}</div><div class="dates">${escapeHtml(exp.dates)}</div></div>`;
      if (exp.achievements?.length) {
        html += `<ul>${exp.achievements.map(a => `<li>${escapeHtml(a)}</li>`).join('')}</ul>`;
      }
    }
  }

  // Education
  if (sections.education?.length) {
    html += `<div class="section-heading">Education</div>`;
    for (const edu of sections.education) {
      html += `<div class="edu-row"><div><strong>${escapeHtml(edu.degree)}</strong> &mdash; ${escapeHtml(edu.institution)}</div><div style="color:#666">${escapeHtml(edu.year)}</div></div>`;
    }
  }

  // Certifications
  if (sections.certifications?.length) {
    html += `<div class="section-heading">Certifications</div>`;
    html += `<ul>${sections.certifications.map(c => `<li>${escapeHtml(c)}</li>`).join('')}</ul>`;
  }

  // Technical Skills
  if (sections.technical_skills && Object.keys(sections.technical_skills).length) {
    html += `<div class="section-heading">Technical Skills</div>`;
    for (const [cat, skills] of Object.entries(sections.technical_skills)) {
      html += `<div class="skills-row"><strong>${escapeHtml(cat)}:</strong> ${skills.map(s => escapeHtml(s)).join(', ')}</div>`;
    }
  }

  html += `</body></html>`;

  // Open in a new window for print-to-PDF
  const printWindow = window.open('', '_blank', 'width=800,height=1100');
  if (!printWindow) {
    emitToast({ type: 'error', message: 'Please allow pop-ups to download the PDF' });
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  // Wait for content to render, then print
  printWindow.onload = () => {
    printWindow.print();
  };
  // Fallback if onload doesn't fire
  setTimeout(() => {
    try { printWindow.print(); } catch { /* already printed */ }
  }, 500);
}
